/**
 * Copyright (c) 2021 MeetOnline.io
 *
 * long description for the file
 *
 * @summary App Component
 * @author Rishabh <rishabh.it.007@gmail.com>
 *
 * Created at     : 2021-02-26 12:00:00
 * Last modified  : 2021-02-26 12:00:00
 */
import React, { ReactNode, useContext, useEffect, useState } from "react"
import { Header } from "../header/index"
import { Home } from "../views/home"
import MeetingRoom from "../views/meetingRoom"
import { Switch, Route, useLocation } from "react-router-dom"
import Bye from "../views/bye"
import NotFound from "../views/404"
import { Routes, Views } from "../../constants"
import { viewSelector } from "../../recoil/atoms/view"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import Emoji from "../common/emoji"
import layoutStates from "../../recoil/atoms/layout"
import { NetworkOverlay } from "../common/overlay"
import { Recording } from "../views/recording"
import { handlePageVisibilityStatusSelector } from "../../recoil/atoms/wsStatus"
import GlobalStyle from "../../globalStyles"
import { SoundAudioList } from "../../hooks/useSoundAlert"
import DevicePermissionOverlay from "../settings/DevicePermissionOverlay"
import SessionEndModal from "../modal/SessionEndModal"
import Shows from "../views/shows"
import { useWindowSizeListener } from "../../hooks/useWindowSize"
import Journey from "../views/journey"
import { MaybeReactScrollMaybeNative } from "../common/styledScrollableList"
import { Plans } from "../views/plans"
import ConnectMOG from "../common/mog"
import Authentication from "../../conditionals/Authentication"
import AuthenticationLayoutContainer from "src/conditionals/Authentication/components/AuthenticationLayoutContainer"
import ProtectedRoute from "src/conditionals/ProtectedRoute"
import {
  isRoomHashPath,
  isSubTopicPage,
  journeyUiHistoryState,
  journeyUiSelector
} from "src/recoil/atoms/journeyUI"
import LoaderConditional from "src/conditionals/LoaderConditional"
import { Account } from "../views/account"
import { ErrorBoundary } from "react-error-boundary"
import ErrorPage from "../common/errors/page"
import AvatarTabNavigator from "../views/avatars"
import { SDKContext } from "src/context/SDKContext"
import * as Sentry from "@sentry/react"
import { useIsIOSApp } from "src/util/deviceType"
import useConfig from "src/hooks/useConfig"
import { ConsoleLogger } from "src/util/logger"
import LazyPreloadedIframe from "src/conditionals/Avatar/LazyPreloadedIframe"
import { DevTest } from "../views/DevTest"
import { ConditionalMeta } from "../ConditionalMeta"

const logger = new ConsoleLogger()

const App = () => {
  const [view, setView] = useRecoilState(viewSelector)
  const [showHeader] = useRecoilState(layoutStates.showHeader)
  const meetingClient = useContext(SDKContext)
  const { sentryEnabled } = useConfig()

  useEffect(() => {
    meetingClient?.nativeBridge.eventNativeSafeAreaBoundsRequested()
  }, [meetingClient])

  // This hook listen resize event and update screenSize State
  useWindowSizeListener()

  const setVisibilityStatus = useSetRecoilState(
    handlePageVisibilityStatusSelector
  )

  const isIos = useIsIOSApp()

  useEffect(() => {
    logger.log(
      `sentryEnabled === true && !Sentry.isInitialized() : ${
        sentryEnabled === true
      } && ${!Sentry.isInitialized()}`
    )
    if (sentryEnabled === true && !Sentry.isInitialized()) {
      logger.log("sentry init")
      const sentryIntegrations = []

      sentryIntegrations.push(Sentry.browserTracingIntegration())
      sentryIntegrations.push(Sentry.browserProfilingIntegration())
      // if (isIos) {
      //   sentryIntegrations.push(Sentry.captureConsoleIntegration())
      // }
      // sentryIntegrations.push(Sentry.replayIntegration()) // commented out for now to avoid running into free tier limit

      Sentry.init({
        enabled: process.env.NODE_ENV === "production",
        dsn: "https://fa1c95b1667b3c0caed367dc9350251d@o4507889122934784.ingest.de.sentry.io/4507889136238672",
        integrations: sentryIntegrations,
        tracesSampleRate: 1.0,
        tracePropagationTargets: [/^https:\/\/utta\.\/app/],
        profilesSampleRate: 1.0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.5
      })
    }
    return () => {
      if (Sentry.isInitialized()) {
        Sentry.close()
      }
    }
  }, [sentryEnabled])

  useEffect(() => {
    document.onvisibilitychange = () => {
      setVisibilityStatus({ status: !window.document.hidden })
    }
  }, [setVisibilityStatus])

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }

    document.addEventListener("touchstart", handleTouchStart, {
      passive: false
    })

    return () => {
      document.removeEventListener("touchstart", handleTouchStart)
    }
  }, [])

  const displayComponent = React.useCallback((view: string) => {
    switch (view) {
      case Views.MEETING_ROOM:
        return <MeetingRoom />
      case Views.RECORDING_VIEW:
        return <Recording />
      case Views.BYE:
        return <Bye />
      case Views.PLANS_VIEW:
        return <Plans />
      case Views.AVATARS_VIEW:
        return <AvatarTabNavigator />
      case Views.HOME:
        return <Home />
      case Views.NOT_FOUND:
        return <NotFound />
      default:
        return <LoaderConditional />
    }
  }, [])

  return (
    <ErrorBoundary fallback={<ErrorPage />}>
      <ConditionalMeta />
      <GlobalStyle />
      <NetworkOverlay />
      <DevicePermissionOverlay />
      <SessionEndModal />
      <ConnectMOG>
        <PopState>
          <Switch>
            <Route path={["/dev-test"]} render={() => <DevTest></DevTest>} />
            <Route
              path={[
                Routes.LOGIN,
                Routes.REGISTER,
                Routes.VERIFY,
                Routes.PASSWORD_RESET
              ]}
              component={AuthenticationRoutes}
            />
            <Route
              path={Routes.AVATARS}
              render={() => <AvatarTabNavigator />}
            />

            <Route
              path={Routes.ACCOUNT}
              render={() => (
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              )}
            />

            <Route
              path={Routes.PLANS}
              render={() => (
                <ProtectedRoute>
                  <Plans />
                </ProtectedRoute>
              )}
            />

            <Route path={Routes.SHOWS} render={() => <Shows />} />
            <Route
              path={Routes.JOURNEY}
              render={() => (
                <ProtectedRoute>
                  <MaybeReactScrollMaybeNative>
                    <Journey />
                  </MaybeReactScrollMaybeNative>
                </ProtectedRoute>
              )}
            />
            <Route path={Routes.LESSON} exact render={() => <Home />} />
            <Route
              path={Routes.HOME}
              render={() => (
                <>
                  {showHeader && <Header />}
                  {displayComponent(view)}
                </>
              )}
            />
          </Switch>
        </PopState>
      </ConnectMOG>
      <Emoji />
      <SoundAudioList />

      {/* <LazyPreloadedIframe /> */}
    </ErrorBoundary>
  )
}

function AuthenticationRoutes() {
  const location = useLocation()

  const [display, setDisplay] = useState(true)

  useEffect(() => {
    setDisplay(
      location.pathname === Routes.LOGIN ||
        location.pathname === Routes.REGISTER ||
        location.pathname === Routes.VERIFY ||
        location.pathname === Routes.PASSWORD_RESET
    )
  }, [location.pathname])

  if (!display) return null
  const hostParts = window.location.host.split(".")
  const hasSubdomain = hostParts.length > 2
  if (hasSubdomain) {
    // react router only handles internal navigation so we set window.location.href instead
    window.location.href = `https://${hostParts[1]}.${hostParts[2]}${location.pathname}?partner=${hostParts[0]}`
  }
  return (
    <AuthenticationLayoutContainer>
      <Switch>
        <Route
          path={Routes.LOGIN}
          render={() => <Authentication type="login" />}
        />
        <Route
          path={Routes.REGISTER}
          render={() => <Authentication type="register" />}
        />
        <Route
          path={Routes.VERIFY}
          render={() => <Authentication type="verify" />}
        />
        <Route
          path={Routes.PASSWORD_RESET}
          render={() => <Authentication type="password-reset" />}
        />
      </Switch>
    </AuthenticationLayoutContainer>
  )
}

function PopState({ children }: { children: ReactNode }) {
  const [stateHistory] = useRecoilState(journeyUiHistoryState)
  const [state, setJourneyUi] = useRecoilState(journeyUiSelector)
  const [currentPathname, setCurrentPathname] = useState("")

  useEffect(() => {
    setCurrentPathname(location?.pathname)
    window.history.pushState = new Proxy(window.history.pushState, {
      apply: (target, thisArg, argArray) => {
        setCurrentPathname(argArray.at(-1))
        // @ts-ignore - TODO: figure out how to resolve the type error from argArray potentially not being long enough -- target has 2 required args
        return target.apply(thisArg, argArray)
      }
    })
  }, [location?.pathname])

  function navigateBackToJourney() {
    const previous = stateHistory.at(-1) as { id: number }
    if (previous.id) {
      setJourneyUi({
        transition: "pageload",
        route: "subtopics",
        id: previous.id
      })
    } else {
      setJourneyUi({ transition: "pageload", route: "topics" })
    }
  }

  useEffect(() => {
    const popState = (e: PopStateEvent) => {
      if (
        isRoomHashPath(currentPathname) &&
        state.transition === "subtopicsToLessons"
      ) {
        navigateBackToJourney()
        return
      }

      if (isSubTopicPage(currentPathname)) {
        setJourneyUi({ transition: "subtopicsToTopics" })
        return
      }

      if (
        currentPathname === Routes.AVATARS ||
        currentPathname === Routes.PLANS
      ) {
        navigateBackToJourney()
        return
      }
    }
    window.addEventListener("popstate", popState)

    return () => {
      window.removeEventListener("popstate", popState)
    }
  }, [stateHistory, state, currentPathname])
  return <>{children}</>
}

export default App
